<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import $ from 'jquery';
import { computed, nextTick, shallowRef, unref, watch } from 'vue';

import { useGenerateIds } from '#public_ts/composables';

import type { SelectExtendedItem, SelectType } from './radio-list';
import { isGroupHeader, isOptionItem, itemsToArray } from './radio-list';

const props = defineProps<{
    name?: string;
    modelValue?: string | null;
    type?: SelectType;
    search?: string;
    withTitles?: boolean;
    withMargin?: boolean;
    withCustomValue?: boolean;
    focus?: number;
}>();
const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
}>();
const selectedItem = useVModel(props, 'modelValue', emit, { passive: true });
const listElement = shallowRef<HTMLElement | undefined>();

const generateIds = useGenerateIds();
const _name = props.name ?? generateIds();
const _customId = generateIds();
const _customSeacrhId = generateIds();

const options = computed(() => itemsToArray(props.type, generateIds));

const customValue = computed(() => {
    if(!props.withCustomValue) return undefined;
    const _value = unref(selectedItem);
    if(!_value) return undefined;

    const _isCustomValue = !unref(options).some((item) => {
        if(!isOptionItem(item)) {
            return false;
        }
        return item.id === _value;
    });
    const _customValue: SelectExtendedItem | undefined = _isCustomValue ? {
        id: _value,
        title: '',
        text: _value,
        genid: _customId,
    } : undefined;

    return _customValue;
});

const filteredOptions = computed(() => {
    const _searchQuery = unref(props.search)?.trim()?.toLowerCase();

    let _filtered: SelectExtendedItem[];
    if(!_searchQuery) {
        _filtered = unref(options);
    } else {
        _filtered = unref(options).filter((item) => {
            if(!isOptionItem(item)) {
                return true;
            }
            if(!item.text && (!item.title || item.title === '--')) {
                return true;
            }
            return item.title?.toLowerCase().includes(_searchQuery)
            || item.text?.toLowerCase().includes(_searchQuery);
        });
    }

    const _customValue = unref(customValue);
    // otherwise two comboboxes
    const _customValues: SelectExtendedItem[] = (_customValue && (_customValue.id !== props.search) ? [_customValue] : []);

    return _customValues.concat(_filtered);
});

watch(() => props.focus, () => {
    if(!props.modelValue) {
        return;
    }
    nextTick(() => {
        setTimeout(() => {
            const selectedRadio = $(unref(listElement) ?? []).find(
                `input[value="${props.modelValue}"]`,
            );
            if(selectedRadio.length == 1) {
                selectedRadio[0]?.focus();
            }
        }, 50);
    });
});

</script>
<template>
    <div class="form_row">
        <ul ref="listElement" class="list-group width-100" :class="{ 'superfacile-radio-no-margin': !props.withMargin }">
            <li v-if="props.withCustomValue && props.search" class="list-group-item">
                <div class="superfacile-option-item">
                    <label :for="_customSeacrhId" class="superfacile-radio-inline">
                        <input
                            :id="_customSeacrhId"
                            v-model="selectedItem"
                            type="radio"
                            :name="_name"
                            :value="props.search"
                        >
                        <span>
                            {{ props.search }}
                        </span>
                    </label>
                    <div class="superfacile-option-buttons">
                        <slot name="buttons" :option="customValue && (customValue.id === props.search) ? customValue : undefined" />
                    </div>
                </div>
            </li>

            <li
                v-for="option in filteredOptions"
                :key="option.genid"
                class="list-group-item"
                :class="isGroupHeader(option) ? (option.classList ?? 'active') : []"
            >
                <div v-if="isGroupHeader(option)" class="superfacile-option-group">
                    <strong>{{ option.group }}</strong>
                </div>
                <div v-if="isOptionItem(option)" class="superfacile-option-item">
                    <label :for="option.genid" class="superfacile-radio-inline">
                        <input :id="option.genid" v-model="selectedItem" type="radio" :name="_name" :value="option.id">
                        <span>
                            <strong v-if="props.withTitles && option.title && (option.title !== '--')">
                                {{ option.title }}
                                {{ option.text ? ' - ' : '' }}
                            </strong>
                            {{ option.text }}
                        </span>
                    </label>
                    <div class="superfacile-option-buttons">
                        <slot name="buttons" :option="option" />
                    </div>
                </div>
            </li>
        </ul>
        <div class="clearfix" />
    </div>
</template>
